import { useState, useEffect } from "react";
import { Download, X, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function InstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if dismissed recently (within 7 days)
    const dismissedAt = localStorage.getItem('pwa-install-dismissed');
    if (dismissedAt) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return; // Don't show if dismissed within 7 days
      }
    }

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      // Show prompt after 3 seconds of using the app
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show prompt after delay if not standalone
    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
        setInstallPrompt(null);
      }
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Don't show if already installed or shouldn't show
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div 
      className="install-prompt-banner"
      data-testid="install-prompt"
    >
      <div className="install-prompt-content">
        <div className="install-prompt-icon">
          <Smartphone className="w-6 h-6 text-amber-600" />
        </div>
        <div className="install-prompt-text">
          <p className="install-prompt-title">Install Eurokon App</p>
          <p className="install-prompt-subtitle">
            {isIOS 
              ? "Tap Share, then 'Add to Home Screen'" 
              : "Quick access from your home screen"
            }
          </p>
        </div>
      </div>
      <div className="install-prompt-actions">
        {!isIOS && installPrompt && (
          <Button 
            size="sm" 
            onClick={handleInstall}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="install-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            Install
          </Button>
        )}
        <button 
          onClick={handleDismiss}
          className="install-prompt-close"
          aria-label="Dismiss"
          data-testid="dismiss-install"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
